import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
	AgentDecision,
	AgentDecisionRule,
} from "../../domain/eventInstance.interface";

/**
 * Why this submission needs a human — frame `4d`, SIMS-8230 AC2.
 *
 * Displays SIMS-8215's stored decision record and nothing else. 🔴 Every value
 * is read from the record, never re-derived: F6 AC6 requires the rule,
 * confidence and thresholds be shown "as read from the stored decision record
 * and never from current configuration", so a submission reviewed months later
 * shows the line that existed when it was decided. Substituting the event's
 * current agent configuration would render a reviewer today's 0.60 threshold
 * against a historical 0.62 — a screen that looks entirely reasonable and is
 * wrong about a decision that moved money.
 *
 * 🔴 The confidence band is OPTIONAL and its absence is a first-class state.
 * Two of the three escalating rules carry no confidence at all:
 * `AGENT_ERROR_OR_TIMEOUT` (SIMS-8215 AC4, which is deliberately not
 * configurable) and `REPEATED_REJECTS` (AC5). The error case is what a
 * coordinator sees when the agent is broken — which is exactly when this panel
 * has to render — so the scale is omitted rather than faked with a zero.
 *
 * 🔴 `agentReason` is model-generated, PHI-derived free text (CMP-8). It is
 * rendered to the reviewer and must never be logged or sent to analytics.
 */

type Props = {
	decision: AgentDecision;
};

/**
 * The rule as a sentence a clinician can act on.
 *
 * SIMS-8215 AC7 stores the rule as a discriminable value precisely so this
 * distinction survives to the reviewer. The fallback turns an unrecognised rule
 * into words rather than showing a raw enum — a new rule added to the policy
 * renders readably without this file being edited, the same approach
 * SIMS-8208's `describeCode` takes.
 */
export const describeRule = (rule: AgentDecisionRule | string): string => {
	const known: Record<string, string> = {
		CONFIDENCE_AT_OR_ABOVE_ACCEPT: "The agent was confident this was valid.",
		CONFIDENCE_BELOW_ACCEPT: "The agent wasn't confident enough to decide.",
		CONFIDENCE_BELOW_ESCALATE: "The agent was confident this was not valid.",
		AGENT_ERROR_OR_TIMEOUT: "The agent could not complete its check.",
		REPEATED_REJECTS: "This has been rejected several times in a row.",
	};

	if (known[rule]) return known[rule];

	const words = rule.toLowerCase().replace(/_/g, " ");

	return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The one-line policy statement under the headline, e.g.
 * "Meal photo agent v3 · policy: confidence below the accept threshold."
 *
 * The agent half is stored; the policy half is derived from the rule, because
 * the record holds no such sentence.
 */
export const describePolicy = (rule: AgentDecisionRule | string): string => {
	const known: Record<string, string> = {
		CONFIDENCE_AT_OR_ABOVE_ACCEPT:
			"confidence at or above the accept threshold",
		CONFIDENCE_BELOW_ACCEPT: "confidence below the accept threshold",
		CONFIDENCE_BELOW_ESCALATE: "confidence below the escalate threshold",
		AGENT_ERROR_OR_TIMEOUT: "agent error or timeout",
		REPEATED_REJECTS: "repeated rejections",
	};

	return known[rule] ?? rule.toLowerCase().replace(/_/g, " ");
};

/** A confidence is only a band when there is a band to place it in. */
const hasBand = (decision: AgentDecision): boolean =>
	typeof decision.confidence === "number" &&
	typeof decision.acceptThreshold === "number" &&
	typeof decision.escalateThreshold === "number";

/**
 * The three values a band needs, taken as required.
 *
 * 🔴 Not `?? 0`. A zero fallback would draw a bar pinned at the left against
 * thresholds that never existed — telling the reviewer the agent was CERTAIN
 * the submission was worthless, which is the opposite of the two rules that
 * escalate without a confidence at all (SIMS-8215 AC4: agent error or timeout,
 * and repeated rejects). Requiring them here moves the guard from a comment to
 * the compiler: a caller that has not narrowed cannot construct this component.
 */
const ConfidenceScale: React.FC<{
	confidence: number;
	accept: number;
	escalate: number;
}> = ({ confidence, accept, escalate }) => {
	// The track is the 0..1 confidence domain. A value outside it would otherwise
	// paint outside the bar, so positions are clamped while the PRINTED numbers
	// stay exactly as recorded — the reader must see the real value even if the
	// drawing cannot place it.
	const pct = (v: number): number => Math.min(100, Math.max(0, v * 100));

	return (
		<Box sx={{ mt: 1.5 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="body2" color="text.secondary">
					Confidence
				</Typography>
				<Typography
					variant="h6"
					sx={{ fontVariantNumeric: "tabular-nums" }}
					data-testid="agent-confidence-value"
				>
					{confidence.toFixed(2)}
				</Typography>
			</Box>

			<Box
				aria-hidden
				sx={{
					position: "relative",
					height: 8,
					borderRadius: 1,
					backgroundColor: "action.hover",
					mt: 0.5,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						left: 0,
						top: 0,
						bottom: 0,
						width: `${pct(confidence)}%`,
						borderRadius: 1,
						backgroundColor: "success.main",
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						left: `${pct(escalate)}%`,
						top: -2,
						bottom: -2,
						width: 2,
						backgroundColor: "error.main",
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						left: `${pct(accept)}%`,
						top: -2,
						bottom: -2,
						width: 2,
						backgroundColor: "text.primary",
					}}
				/>
			</Box>

			{/*
			 * Both thresholds are labelled, and both are the STORED values. This
			 * line is the whole of AC2's "positioned between the accept and
			 * escalate thresholds that were in force at decision time".
			 */}
			<Box
				sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
				data-testid="agent-thresholds"
			>
				<Typography variant="caption" color="text.secondary">
					Escalate below {escalate.toFixed(2)}
				</Typography>
				<Typography variant="caption" color="text.secondary">
					Accept at {accept.toFixed(2)}
				</Typography>
			</Box>
		</Box>
	);
};

const AgentContextPanel: React.FC<Props> = ({ decision }) => {
	const band = hasBand(decision);

	return (
		<Box sx={{ mb: 2 }} data-testid="agent-context-panel">
			<Typography variant="subtitle1" sx={{ mb: 1 }}>
				Why this needs review
			</Typography>

			<Paper
				variant="outlined"
				/*
				 * Neutral, deliberately. Frame `4d` tints this border with Glyra's
				 * brand green, and the obvious binding is `success.light` — but that
				 * is the SEMANTIC success token, and this panel says a submission
				 * could not be decided and needs a human. Green here tells the
				 * reviewer the opposite of what the panel means, and under any theme
				 * whose brand is not green it reads as plainly wrong.
				 */
				sx={{ p: 2, borderColor: "divider", borderWidth: 2 }}
			>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
					<SmartToyOutlinedIcon fontSize="small" sx={{ mt: 0.25 }} />
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle2">
							{describeRule(decision.rule)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{decision.agentKey} {decision.agentVersion} · policy:{" "}
							{describePolicy(decision.rule)}.
						</Typography>
					</Box>
				</Box>

				{band ? (
					<ConfidenceScale
						confidence={decision.confidence as number}
						accept={decision.acceptThreshold as number}
						escalate={decision.escalateThreshold as number}
					/>
				) : (
					/*
					 * 🔴 NOT an empty state and NOT a failure to load. The policy
					 * reached this outcome without a confidence judgement, so saying
					 * so is the honest rendering. Showing a 0.00 against thresholds
					 * that never existed would tell the reviewer the agent was
					 * certain the submission was worthless.
					 */
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 1.5 }}
						data-testid="agent-no-confidence"
					>
						No confidence score was recorded for this decision.
					</Typography>
				)}

				{decision.agentReason && (
					<Box sx={{ mt: 2 }}>
						<Typography variant="body2" color="text.secondary">
							Agent reasoning
						</Typography>
						{/*
						 * The agent's own words, framed as a quotation so a reviewer
						 * reads them as the model's account rather than the platform's
						 * finding. PHI-derived free text — display only.
						 */}
						<Paper
							variant="outlined"
							sx={{ p: 1.5, mt: 0.5, backgroundColor: "action.hover" }}
						>
							<Typography variant="body2" data-testid="agent-reason">
								{decision.agentReason}
							</Typography>
						</Paper>
					</Box>
				)}

				{typeof decision.priorConsecutiveRejects === "number" &&
					decision.priorConsecutiveRejects > 0 && (
						<Chip
							size="small"
							variant="outlined"
							sx={{ mt: 1.5 }}
							label={`${decision.priorConsecutiveRejects} previous rejection${
								decision.priorConsecutiveRejects === 1 ? "" : "s"
							} in a row`}
						/>
					)}
			</Paper>
		</Box>
	);
};

export default AgentContextPanel;
